import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { parseISO } from 'date-fns'
import { useSelector } from 'react-redux'
import { shortTime, progress } from '../../utils/time'
import { Image, ProgressBar } from '..'
import styles from './styles'
import { LiveIcon, PadlockIcon } from '../icons'
import { colors } from '../../styles'
import { trackEvent } from '../../utils/dtmHelpers'
import getSportsSubtitle from '../../utils/strings'

const NewScheduleCard = ({
  title,
  summary,
  startTime,
  endTime,
  link,
  image,
  position,
  channel,
  isOverlay,
  isTvGuide,
  logo,
  sportsExclusive,
}) => {
  // eslint-disable-next-line no-unused-vars
  const users = useSelector(state => state.user)
  const sportsEntitlementError = sportsExclusive && (!users.currentlySubscribed || (users.currentlySubscribed && users.subscriptionType !== 'premier_sports'))
  const programmeProgress = progress({
    start: parseISO(startTime).getTime(),
    end: parseISO(endTime).getTime(),
    current: Date.now(),
  })
  const [remainingTime, setRemainingTime] = useState('')

  useEffect(() => {
    // Function to calculate remaining time
    const calculateRemainingTime = () => {
      if (!endTime) {
        return
      }
      const endDate = new Date(endTime)
      const currentDate = new Date()
      const remainingMilliseconds = endDate - currentDate
      const remainingHours = Math.floor(remainingMilliseconds / (1000 * 60 * 60))
      const remainingMinutes = Math.floor((remainingMilliseconds % (1000 * 60 * 60)) / (1000 * 60))
      // Format the remaining time
      const formattedRemainingTime = `${remainingHours <= 0 ? `${remainingMinutes} mins left` : `${remainingHours} hrs ${remainingMinutes} mins left`}`
      setRemainingTime(formattedRemainingTime)
    }
    calculateRemainingTime()
    const intervalId = setInterval(calculateRemainingTime, 60000)
    return () => clearInterval(intervalId)
  }, [startTime, endTime])

  const analyticsTracking = () => {
    if (!isOverlay) {
      trackEvent('homepageClick', { item: `TV Guide Live,99,${position || 1},${channel}` })
    } else {
      trackEvent('homepageClick', { item: `Live Nav Item,99,${position || 1},${channel}` })
    }
  }

  const handleClick = async (e) => {
    e.preventDefault()
    analyticsTracking()
    // Navigate to the link if not PS or no concurrent stream
    window.location.href = link
  }

  return (
    <div className={`ScheduleCard ${!isTvGuide ? 'overlay' : 'tv-guide'}`}>
      <figure className={`figure-wrapper ${isOverlay ? 'overlay' : ''}`}>
        <a href={link} onClick={handleClick}>
          <div className="image-wrapper">
            <Image additionalStyles={{ borderRadius: '16px' }} image={image} sizes={'100vw'} />
            {sportsEntitlementError && users.gettingDetails && (
              <div className="ps-overlay">
                <PadlockIcon color="#FFFF00" width={16} height={19} /> <span>{getSportsSubtitle(title)}</span>
              </div>
            )}
          </div>

          <figcaption className={isOverlay ? 'overlay' : ''}>
            <div className={`live-player ${isOverlay ? 'overlay' : ''}`}>
              <LiveIcon />
              <p
                className="live-subtitle"
                style={{ color: colors.primaryText }}
              >
                LIVE
              </p>
            </div>
            {remainingTime && <div className={`time-left ${isOverlay ? 'overlay' : ''}`}>{remainingTime}</div>}
          </figcaption>
          <div className="live-gradient">
            <div className={`progress-bar live ${isOverlay ? 'overlay' : ''}`}>
              <ProgressBar progress={programmeProgress} color={colors.liveRed} bgColor={'rgba(246, 246, 246, 0.20)'} />
            </div>
          </div>
          <div className={`bottom-wrapper ${isOverlay ? 'overlay' : ''}`}>
            <div className="bottom-image">
              <figure className={`schedule ${isOverlay ? 'overlay' : ''}`}>
                {Object.keys(logo).length !== 0 && <Image image={logo} width={41} height={41} sizes={'100vw'} />}
              </figure>
            </div>
            <div className="bottom-data">
              <div className={`main-content ${isOverlay ? 'overlay' : ''}`}>
                <h3
                  className={`title ${isOverlay ? 'overlay' : ''}`}
                >
                  {title}
                </h3>
                {startTime && endTime && (
                  <span className={`time ${isOverlay ? 'overlay' : ''}`}>
                    {shortTime(startTime)} - {shortTime(endTime)}
                  </span>
                )}
                {isTvGuide && <p className="summary">{summary}</p>}
              </div>
            </div>
          </div>
        </a>
      </figure>
      <style jsx>{styles}</style>
    </div>
  )
}

NewScheduleCard.propTypes = {
  title: PropTypes.string.isRequired,
  summary: PropTypes.string.isRequired,
  startTime: PropTypes.string.isRequired,
  endTime: PropTypes.string.isRequired,
  link: PropTypes.string,
  image: PropTypes.object,
  position: PropTypes.number,
  channel: PropTypes.string,
  isOverlay: PropTypes.bool,
  isTvGuide: PropTypes.bool,
  logo: PropTypes.object,
  sportsExclusive: PropTypes.bool,
}

NewScheduleCard.defaultProps = {
  link: '',
  image: {},
  position: 1,
  channel: 'STV',
  isOverlay: false,
  isTvGuide: false,
  logo: {},
  sportsExclusive: false,
}

export default NewScheduleCard
